import React, { useContext, useEffect } from "react";
import Link from "@material-ui/core/Link";
import LogoSquare from '../assets/plagaware-logo-square-50x50.png';
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import Service from "../services/service";
import { showCaptcha, config, resolveNotificationMessage } from '../helpers/constants';
import { FormHelperText, Paper, Grid, Typography, Button, TextField } from "@material-ui/core";
import FullScreenPage from "./FullScreenPage";
import LoadingOverlayButton from './LoadingOverlayButton';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 3),
    height: 48
  },
}));

export default function ResetPassword(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const classes = useStyles();
  const [userDetail, setUserDetail] = React.useState({
    userName: '',
    password: '',
    resetCode: '',
  });
  const [step, setStep] = React.useState(0);
  const s = state.strings;

  useEffect(() => {
    if (!state.user.loggedIn) {
      showCaptcha();
    }
  }, []);

  const captchaAction = (action, fn, onFail) => {
    const captchas = document.getElementsByClassName("grecaptcha-badge");
    if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
          Service.verifyReCaptcha(token).then((verifyRes) => {
            if (verifyRes.success) {
              fn();
            } else {
              if (onFail) onFail();
              dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
            }
          });
        });
      });
    } else {
      fn();
    }
  }

  const doSendCode = () => {
    Service.sendPasswordResetMail(userDetail.userName).then(res => {
      setSendCodeLoadingSync(false);
      if (res.success) {
        dispatch(actions.openToast({ type: "success", message: s.loginDialog.mailSent, status: true }));
        setStep(1);
        setSendCodeDelay(30);
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  const handleSendCode = () => {
    if (sendCodeLoadingRef.current) return;
    setSendCodeLoadingSync(true);
    captchaAction("resetPassword", doSendCode, () => setSendCodeLoadingSync(false));
  }

  const [sendCodeLoading, setSendCodeLoading] = React.useState(false);
  const sendCodeLoadingRef = React.useRef(false);
  const setSendCodeLoadingSync = (v) => { sendCodeLoadingRef.current = v; setSendCodeLoading(v); };
  const [sendCodeDelay, setSendCodeDelay] = React.useState(0);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const loginLoadingRef = React.useRef(false);
  const setLoginLoadingSync = (v) => { loginLoadingRef.current = v; setLoginLoading(v); };

  // countdown timer for resend cooldown
  useEffect(() => {
    if (sendCodeDelay <= 0) return;
    const id = setInterval(() => setSendCodeDelay(d => Math.max(0, d - 1)), 1000);
    return () => clearInterval(id);
  }, [sendCodeDelay > 0]);

  const doLogin = () => {
    Service.login(userDetail.userName, null, userDetail.resetCode).then(res => {
      if (res.success) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          dispatch(actions.navigate_to("/dashboard"));
          dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedInReset.format(getInfoRes.data.mail), status: true }));
        });
      } else {
        setLoginLoadingSync(false);
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  const handleLogin = () => {
    if (loginLoadingRef.current) return;
    setLoginLoadingSync(true);
    captchaAction("login", doLogin, () => setLoginLoadingSync(false));
  }

  const handleChange = (value, key) => {
    setUserDetail({
      ...userDetail,
      [key]: value
    })
  }

  const buttonLabels = [s.loginDialog.sendCode, s.loginDialog.loginWithCode];

  return (
    <FullScreenPage>
      <Container component="main" style={{width:450}}>
        <Paper className={classes.paper}>
          <img src={LogoSquare} alt="logo" style={{ margin: 16 }} />
          <Typography component="h1" variant="h5" paragraph>
            {s.loginDialog.forgotPwdHeading}
          </Typography>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label={s.loginDialog.mail}
            name="email"
            autoComplete="email"
            autoFocus
            value={userDetail.userName}
            onChange={(evt) => handleChange(evt.target.value, 'userName')} />
          {(step == 0) &&
            <>
              <FormHelperText>{s.loginDialog.enterMailReset}</FormHelperText>
              <LoadingOverlayButton
                fullWidth
                variant="contained"
                color="primary"
                loading={sendCodeLoading}
                disabled={userDetail.userName.length < 6}
                className={classes.submit}
                onClick={handleSendCode}>
                {buttonLabels[step]}
              </LoadingOverlayButton>
            </>
          }
          {(step == 1) &&
            <>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="resetCode"
                label={s.loginDialog.resetCode}
                id="resetCode"
                autoComplete="current-password"
                value={userDetail.resetCode}
                onChange={(evt) => handleChange(evt.target.value, 'resetCode')} />
              <FormHelperText>{s.loginDialog.mailSentTip}</FormHelperText>
              {sendCodeDelay > 0
                ? <Typography variant="body2">{s.loginDialog.reSendCode} ({sendCodeDelay})</Typography>
                : <Link component="button" type="button" disabled={sendCodeLoading} onClick={handleSendCode} variant="body2">{s.loginDialog.reSendCode}</Link>
              }
              <LoadingOverlayButton
                fullWidth
                variant="contained"
                color="primary"
                loading={loginLoading}
                disabled={userDetail.resetCode.trim().length != 12}
                className={classes.submit}
                onClick={handleLogin}>
                {buttonLabels[step]}
              </LoadingOverlayButton>
            </>
          }

          <Grid container>
            <Grid item xs>
              <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/login'))}>
                {s.loginDialog.login}
              </Link>
            </Grid>
            <Grid item>
              <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/signup'))}>
                {s.loginDialog.signUpLink}
              </Link>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs>
            </Grid>
            <Grid item>
              {state.user.loggedIn &&
                <Link component="button" type="button" variant="body2" onClick={() => dispatch(actions.navigate_to('/dashboard'))}>
                  {s.loginDialog.continueAs.format(state.user.mail)}
                </Link>
              }
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </FullScreenPage>
  );
}
